import { DeepValueOf } from '@/utils/types-utils'

export const ENDPOINTS = {
  ACTIVITY_LOGS: {
    DELETE: '/activity-logs',
    GET_ALL: '/activity-logs',
    GET_BY_ID: (activityLogId: number | string) => `/activity-logs/${activityLogId}`
  },
  ADMINS: {
    BULK_CHANGE_STATUS: '/admins/bulk-change-status',
    BULK_DELETE: '/admins/bulk-delete',
    CREATE: '/admins',
    DEACTIVATE: (adminId: number | string) => `/admins/${adminId}/deactivate`,
    DELETE: (adminId: number | string) => `/admins/${adminId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/admins',
    EXPORT: '/admins/export',
    GET_ALL: '/admins',
    GET_BY_ID: (adminId: number | string) => `/admins/${adminId}`,
    IMPORT: '/admins/import',
    IMPORT_VALIDATE: '/admins/import/validate',
    LIST_DROPDOWN: '/admins/list-dropdown',
    UPDATE: (adminId: number | string) => `/admins/${adminId}?_method=put`
  },
  AREA: {
    CITIES_LIST_DROPDOWN: '/cities/list-dropdown',
    COUNTRIES_LIST_DROPDOWN: '/countries/list-dropdown',
    DELETE_CITY: (cityId: number | string) => `/cities/${cityId}`,
    DELETE_COUNTRY: (countryId: number | string) => `/countries/${countryId}`,
    DELETE_STATE: (stateId: number | string) => `/states/${stateId}`,
    EXPORT_CITIES: '/cities/export',
    EXPORT_COUNTRIES: '/countries/export',
    EXPORT_STATES: '/states/export',
    GET_ALL_CITIES: '/cities',
    GET_ALL_STATES: '/states',
    GET_BY_ID: (countryId: number | string) => `/countries/${countryId}`,
    GET_CITIES_BY_STATE_ID: (stateId: number) => `/cities/state/${stateId}`,
    GET_CITY_BY_ID: (cityId: number | string) => `/cities/${cityId}`,
    GET_COUNTRIES: '/countries',
    GET_STATE_BY_ID: (stateId: number | string) => `/states/${stateId}`,
    GET_STATES_BY_COUNTRY_ID: (countryId: number | string) => `/states/country/${countryId}`,
    POST_ADD_CITY: '/cities',
    POST_ADD_COUNTRY: '/countries',
    POST_ADD_STATE: '/states',
    PUT_UPDATE_CITY: (cityId: number | string) => `/cities/${cityId}`,
    PUT_UPDATE_COUNTRY: (countryId: number | string) => `/countries/${countryId}`,
    PUT_UPDATE_STATE: (stateId: number | string) => `/states/${stateId}`,
    SEARCH_CITIES: (query: string) => `/cities?search=${encodeURIComponent(query)}`,
    STATES_LIST_DROPDOWN: '/states/list-dropdown'
  },
  AUTH: {
    FORGOT_PASSWORD: '/forgot-password',
    GET_CURRENT_USER: '/auth/get-info',
    GET_CURRENT_USER_PERMISSIONS: '/permissions',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh-token',
    RESEND_OTP: '/resend-otp-code',
    RESET_PASSWORD: '/reset-password',
    UPDATE_INFO: '/auth/update-info',
    UPDATE_LANGUAGE: '/me/update-language',
    VERIFY_OTP: '/verify-otp-code'
  },
  CAR_RENTALS: {
    CANCEL: (rentalId: number | string) => `/car-rentals/${rentalId}`,
    END_SLOT: (slotId: number | string) => `/car-rentals/slots/${slotId}/end`,
    GET_ALL: '/car-rentals',
    GET_BY_ID: (rentalId: number | string) => `/car-rentals/${rentalId}`,
    GET_SLOTS: (rentalId: number | string) => `/car-rentals/${rentalId}/slots`,
    START_SLOT: (slotId: number | string) =>
      `/car-rentals/slots/${slotId}/start`,
    UPDATE: (rentalId: number | string) => `/car-rentals/${rentalId}`,
    UPDATE_SLOT_NOTES: (slotId: number | string) =>
      `/car-rentals/slots/${slotId}/notes`
  },
  CAR_WASHES: {
    CREATE: '/car-washes',
    DELETE: (carWashId: number | string) => `/car-washes/${carWashId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/car-washes',
    EXPORT: '/car-washes/export',
    GET_ALL: '/car-washes',
    GET_BY_ID: (carWashId: number | string) => `/car-washes/${carWashId}`,
    IMPORT: '/car-washes/import',
    IMPORT_VALIDATE: '/car-washes/import/validate',
    LIST_DROPDOWN: '/car-washes/list-dropdown',
    UPDATE: (carWashId: number | string) => `/car-washes/${carWashId}`
  },
  COLORS: {
    LIST_DROPDOWN: '/colors/list-dropdown'
  },
  CONFIG: {
    GET_CONFIG: '/configs',
    GET_TERMS: '/configs/driver-terms',
    PUT_UPDATE_CONFIG: '/configs'
  },
  CONTRACTS: {
    CREATE: '/contracts',
    DELETE: (contractId: number | string) => `/contracts/${contractId}`,
    GET_ALL: '/contracts',
    UPDATE_BY_ID: (contractId: number | string) => `/contracts/${contractId}`
  },
  DASHBOARD: {
    GET_STATISTICS: '/dashboard'
  },
  DRIVERS: {
    ACCEPT_RESIGNATION: (resignationId: number | string) => `resignation-requests/${resignationId}/approve`,
    BULK_CHANGE_STATUS: '/drivers/bulk-change-status',
    BULK_DELETE: '/drivers/bulk-delete',
    CREATE: '/drivers',
    DELETE: (driverId: number | string) => `/drivers/${driverId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/drivers',
    EXPORT: '/drivers/export',
    GET_ALL: '/drivers',
    GET_ALL_RESIGNATION_REQUESTS: '/resignation-requests',
    GET_BY_ID: (driverId: number | string) => `/drivers/${driverId}`,
    GET_RESIGNATION_REQUESTS_BY_DRIVER_ID: (driverId: number | string) => `resignation-requests/driver/${driverId}`,
    IMPORT: '/drivers/import',
    IMPORT_VALIDATE: '/drivers/import/validate',
    LIST_DROPDOWN: '/drivers/list-dropdown',
    REJECT_RESIGNATION: (resignationId: number | string) => `resignation-requests/${resignationId}/reject`,
    TOGGLE_STATUS: (driverId: number | string) => `/drivers/${driverId}/toggle-status`,
    UPDATE: (driverId: number | string) => `/drivers/${driverId}?_method=put`
  },
  EMERGENCIES: {
    EXPORT: '/emergencies/export',
    GET_ALL: '/emergencies',
    GET_BY_ID: (emergencyId: number | string) => `/emergencies/${emergencyId}`,
    RESOLVE: (emergencyId: number | string) => `/emergencies/${emergencyId}/resolve`,
    UPDATE: (emergencyId: number | string) => `/emergencies/${emergencyId}?_method=put`
  },
  FUEL_BRANDS: {
    CREATE: '/fuel-brands',
    DELETE: (fuelBrandId: number | string) => `/fuel-brands/${fuelBrandId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/fuel-brands',
    EXPORT: '/fuel-brands/export',
    GET_ALL: '/fuel-brands',
    GET_BY_ID: (fuelBrandId: number | string) => `/fuel-brands/${fuelBrandId}`,
    IMPORT: '/fuel-brands/import',
    IMPORT_VALIDATE: '/fuel-brands/import/validate',
    LIST_DROPDOWN: '/fuel-brands/list-dropdown',
    UPDATE: (fuelBrandId: number | string) => `/fuel-brands/${fuelBrandId}`
  },
  GAS_STATIONS: {
    CREATE: '/gas-stations',
    DELETE: (gasStationId: number | string) => `/gas-stations/${gasStationId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/gas-stations',
    EXPORT: '/gas-stations/export',
    GET_ALL: '/gas-stations',
    GET_BY_ID: (gasStationId: number | string) => `/gas-stations/${gasStationId}`,
    IMPORT: '/gas-stations/import',
    IMPORT_VALIDATE: '/gas-stations/import/validate',
    LIST_DROPDOWN: '/gas-stations/list-dropdown',
    UPDATE: (gasStationId: number | string) => `/gas-stations/${gasStationId}`
  },

  LEAVE_REQUESTS: {
    APPROVE: (leaveId: number | string) => `/leave-requests/${leaveId}/approve`,
    CREATE: '/leave-requests',
    DELETE: (leaveId: number | string) => `/leave-requests/${leaveId}`,
    EXPORT: '/leave-requests/export',
    GET_ALL: '/leave-requests',
    GET_BY_DRIVER_ID: (driverId: number | string) => `/leave-requests/driver/${driverId}`,
    GET_BY_ID: (leaveId: number | string) => `/leave-requests/${leaveId}`,
    REJECT: (leaveId: number | string) => `/leave-requests/${leaveId}/reject`,
    UPDATE: (leaveId: number | string) => `/leave-requests/${leaveId}`
  },

  MAINTENANCE_ROUTES: {
    ACCEPT: (maintenanceRouteId: number | string) => `/maintenance-routes/${maintenanceRouteId}/accept`,
    CREATE: '/maintenance-routes',
    DELETE: (maintenanceRouteId: number | string) => `/maintenance-routes/${maintenanceRouteId}`,
    EXPORT: '/maintenance-routes/export',
    GET_ALL: '/maintenance-routes',
    GET_BY_ID: (maintenanceRouteId: number | string) => `/maintenance-routes/${maintenanceRouteId}`,
    REJECT: (maintenanceRouteId: number | string) => `/maintenance-routes/${maintenanceRouteId}/reject`,
    UPDATE: (maintenanceRouteId: number | string) => `/maintenance-routes/${maintenanceRouteId}`
  },

  NOTIFICATIONS: {
    GET_ALL: 'notifications',
    GET_BY_ID: (notification_id: number | string) => `notifications/${notification_id}`,
    GET_USERS: (notification_id: number | string) => `notifications/${notification_id}/users`,
    RESEND_NOTIFICATION: (notification_id: number | string) => `notifications/${notification_id}/resend`,
    SEND_NOTIFICATION: 'notifications/send-notification',
    SEND_SCOPED_NOTIFICATION: 'notifications/send-scoped-notification'
  },

  OFFICES: {
    CREATE: '/offices',
    DELETE: (officeId: number | string) => `/offices/${officeId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/offices',
    EXPORT: '/offices/export',
    GET_ALL: '/offices',
    GET_BY_ID: (officeId: number | string) => `/offices/${officeId}`,
    IMPORT: '/offices/import',
    IMPORT_VALIDATE: '/offices/import/validate',
    LIST_DROPDOWN: '/offices/list-dropdown',
    UPDATE: (officeId: number | string) => `/offices/${officeId}`
  },

  PAYSLIP_ATTACHMENTS: {
    DELETE: (attachmentId: number | string) => `/payslip-attachments/${attachmentId}`,
    GET_ALL: '/payslip-attachments',
    UPDATE: (attachmentId: number | string) => `/payslip-attachments/${attachmentId}`
  },
  PAYSLIPS: {
    ATTACHMENTS_BATCHES: '/payslip-attachments/batches/list-dropdown',
    ATTACHMENTS_UPLOAD_BATCH: '/payslips/attachments/upload-batch',
    CREATE: '/payslips',
    DELETE: (payslipId: number | string) => `/payslips/${payslipId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/payslips',
    EXPORT: '/payslips/export',
    GET_ALL: '/payslips',
    GET_BY_DRIVER_ID: (driverId: number | string) => `/payslips/driver/${driverId}`,
    GET_BY_ID: (payslipId: number | string) => `/payslips/${payslipId}`,
    IMPORT: '/payslips/import',
    IMPORT_VALIDATE: '/payslips/import/validate',
    UPDATE: (payslipId: number | string) => `/payslips/${payslipId}?_method=put`
  },
  PERMISSIONS: {
    GET_ALL: '/permissions'
  },
  PUSH_NOTIFICATIONS: {
    DELETE: (notification_id: number | string) => `me/notifications/${notification_id}`,
    GET_ALL: 'me/notifications',
    GET_BY_ID: (notification_id: number | string) => `me/notifications/${notification_id}`,
    MARK_ALL_AS_READ: 'me/notifications/mark-all-as-read',
    MARK_AS_READ: (notification_id: number | string) => `me/notifications/${notification_id}/mark-as-read`,
    RESEND_NOTIFICATION: (notification_id: number | string) => `me/notifications/${notification_id}/resend`,
    SEND_NOTIFICATION: 'me/notifications/send-notification'
  },

  RENTAL_CONTRACTS: {
    CANCEL: (contractId: number | string) => `/rental-contracts/${contractId}`,
    CREATE: '/rental-contracts',
    CREATE_RENTAL: (contractId: number | string) =>
      `/rental-contracts/${contractId}/rentals`,
    EXPORT: '/rental-contracts/export',
    GET_ALL: '/rental-contracts',
    GET_BY_ID: (contractId: number | string) =>
      `/rental-contracts/${contractId}`,
    GET_RENTALS: (contractId: number | string) =>
      `/rental-contracts/${contractId}/rentals`,
    UPDATE: (contractId: number | string) => `/rental-contracts/${contractId}`
  },

  REPORTS: {
    ACTIVE_TRIPS: {
      EXPORT: '/active-trips-reports/export',
      GET_ALL: '/active-trips-reports'
    },
    DRIVER_FEEDBACK: {
      EXPORT: '/driver-feedback-reports/export',
      GET_ALL: '/driver-feedback-reports'
    },
    DRIVER_TRIPS: {
      EXPORT: '/driver-trips-reports/export',
      GET_ALL: '/driver-trips-reports'
    },
    FEEDBACK_TRENDS: {
      EXPORT: '/feedback-trends-reports/export',
      GET_ALL: '/feedback-trends-reports'
    },
    NUMBER_OF_TRIPS: {
      EXPORT: '/number-of-trips-reports/export',
      GET_ALL: '/number-of-trips-reports'
    },
    ROUTE_DETAILS: {
      EXPORT: '/route-details-reports/export',
      GET_ALL: '/route-details-reports'
    },
    SPEED_VIOLATIONS: {
      EXPORT: '/speed-violations-reports/export',
      GET_ALL: '/speed-violations-reports'
    },
    SPEED_VIOLATIONS_PER_DRIVER: {
      EXPORT: '/speed-violations-per-driver-reports/export',
      GET_ALL: '/speed-violations-per-driver-reports'
    },
    TRIP: {
      EXPORT: '/trip-reports/export',
      GET_ALL: '/trip-reports'
    },
    TRIP_BOOKING: {
      EXPORT: '/trip-booking-reports/export',
      GET_ALL: '/trip-booking-reports'
    },
    TRIP_DETAILS: {
      EXPORT: '/trip-details-reports/export',
      GET_ALL: '/trip-details-reports'
    },
    TRIP_KM: {
      EXPORT: '/trip-km-reports/export',
      GET_ALL: '/trip-km-reports'
    },
    TRIP_STOPS: {
      EXPORT: '/trip-stops-reports/export',
      GET_ALL: '/trip-stops-reports'
    },
    VEHICLE: {
      EXPORT: '/vehicle-reports/export',
      GET_ALL: '/vehicle-reports'
    },
    VEHICLE_AVAILABILITY: {
      EXPORT: '/vehicle-availability-reports/export',
      GET_ALL: '/vehicle-availability-reports'
    },
    VEHICLE_FEEDBACK: {
      EXPORT: '/vehicle-feedback-reports/export',
      GET_ALL: '/vehicle-feedback-reports'
    }
  },

  REQUESTS: {
    APPROVE: (requestId: number | string) => `/trip-requests/${requestId}/approve`,
    CREATE: '/trip-requests',
    DELETE: (requestId: number | string) => `/trip-requests/${requestId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/special-trip-requests',
    DYNAMIC: {
      DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/dynamic-trip-requests',
      EXPORT: '/trip-requests/export/dynamic',
      GET_ALL: '/trip-requests/dynamic',
      GET_BY_USER_ID: (userId: number | string) => `/trip-requests/dynamic/user/${userId}`,
      IMPORT: '/trip-requests/import/dynamic',
      IMPORT_VALIDATE: '/trip-requests/import/dynamic/validate'
    },
    EXPORT: '/trip-requests/export/special',
    GET_ALL: '/trip-requests/special',
    GET_BY_ID: (requestId: number | string) => `/trip-requests/${requestId}`,
    GET_BY_USER_ID: (userId: number | string) => `/trip-requests/special/user/${userId}`,
    IMPORT: '/trip-requests/import/special',
    IMPORT_VALIDATE: '/trip-requests/import/special/validate',
    REJECT: (requestId: number | string) => `/trip-requests/${requestId}/reject`,
    // UPDATE: (requestId: number | string) => `/trip-requests/${requestId}`
    UPDATE: (requestId: number | string) => `/trips/${requestId}/dynamic-or-special`
  },
  ROLES: {
    CREATE: '/roles',
    DELETE: (roleId: number | string) => `/roles/${roleId}`,
    GET_ALL: '/roles',
    GET_ALL_WITHOUT_PAGINATION: '/roles/list',
    GET_BY_ID: (roleId: number | string) => `/roles/${roleId}`,
    GET_TENANT_ROLES_LIST: '/tenant-roles/all',
    LIST_DROPDOWN: '/roles/list-dropdown',
    UPDATE: (roleId: number | string) => `/roles/${roleId}`
  },
  ROUTE_SHIFT_PASSENGERS: {
    BULK_CHANGE_STATUS: '/route-shift-passengers/bulk-change-status',
    BULK_DELETE: '/route-shift-passengers/bulk-delete',
    DELETE: (passengerId: number | string) => `/route-shift-passengers/${passengerId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/route-shift-passengers',
    EXPORT: '/route-shift-passengers/export',
    GET_ALL: 'route-shift-passengers',
    GET_BY_ID: (id: number | string) => `/route-shift-passengers/${id}`,
    GET_BY_ROUTE_SHIFT_ID: (shiftId: number | string) => `/route-shift-passengers/route-shift/${shiftId}`,
    GET_BY_STOP_ID: (stopId: number | string) => `/route-shift-passengers/route-shift-stop/${stopId}`,
    IMPORT: (shiftId: number | string) => `/route-shifts/${shiftId}/passengers/import`,
    IMPORT_VALIDATE: (shiftId: number | string) => `/route-shifts/${shiftId}/passengers/import/validate`,
    LIST_DROPDOWN: '/route-shift-passengers/list-dropdown',
    POST_BULK_CREATE: '/route-shift-passengers/bulk',
    POST_BULK_CREATE_BY_USER: '/route-shift-passengers/bulk-by-user',
    POST_CREATE: '/route-shift-passengers',
    PUT_UPDATE: (passengerId: number | string) => `/route-shift-passengers/${passengerId}`
  },
  ROUTE_SHIFT_REROUTE_REQUESTS: {
    APPROVE: (requestId: number | string) => `/route-shift-reroute-requests/${requestId}/approve`,
    EXPORT: '/route-shift-reroute-requests/export',
    GET_ALL: '/route-shift-reroute-requests',
    LIST_DROPDOWN: '/route-shift-reroute-requests/list-dropdown',
    REJECT: (requestId: number | string) => `/route-shift-reroute-requests/${requestId}/reject`
  },
  ROUTE_SHIFTS: {
    BULK_CHANGE_STATUS: '/route-shifts/bulk-change-status',
    BULK_DELETE: '/route-shifts/bulk-delete',
    CREATE: '/route-shifts',
    DELETE: (routeShiftId: number | string) => `/route-shifts/${routeShiftId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/route-shifts',
    DOWNLOAD_IMPORT_TEMPLATE_ROUTE: '/import-templates/route-shifts-route',
    EXPORT: '/route-shifts/export',
    GET_ALL: '/route-shifts',
    GET_BY_DRIVER_ID: (driverId: number | string) => `/route-shifts/driver/${driverId}`,
    GET_BY_ID: (routeShiftId: number | string) => `/route-shifts/${routeShiftId}`,
    GET_BY_ROUTE_ID: (routeId: number | string) => `/route-shifts/tenant-route/${routeId}`,
    GET_BY_TENANT_ID: (tenantId: number | string) => `/route-shifts/tenant/${tenantId}`,
    GET_BY_VEHICLE_ID: (vehicleId: number | string) => `/route-shifts/vehicle/${vehicleId}`,
    IMPORT: '/route-shifts/import',
    IMPORT_BY_ROUTE_ID: (routeId: number | string) => `/tenant-routes/${routeId}/shifts/import`,
    IMPORT_VALIDATE: '/route-shifts/import/validate',
    IMPORT_VALIDATE_BY_ROUTE_ID: (routeId: number | string) => `/tenant-routes/${routeId}/shifts/import/validate`,
    LIST_DROPDOWN: '/route-shifts/list-dropdown',
    UPDATE: (routeShiftId: number | string) => `/route-shifts/${routeShiftId}`,
    UPDATE_POLYLINE: (routeShiftId: number | string) => `/route-shifts/${routeShiftId}/polyline`
  },
  SERVICE_CENTERS: {
    CREATE: '/service-centers',
    DELETE: (serviceCenterId: number | string) => `/service-centers/${serviceCenterId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/service-centers',
    EXPORT: '/service-centers/export',
    GET_ALL: '/service-centers',
    GET_BY_ID: (serviceCenterId: number | string) => `/service-centers/${serviceCenterId}`,
    GET_INDEX_BY_SERVICE_TYPE: (serviceTypeId: number | string) => `/service-centers/service-type/${serviceTypeId}`,
    IMPORT: '/service-centers/import',
    IMPORT_VALIDATE: '/service-centers/import/validate',
    LIST_DROPDOWN: '/service-centers/list-dropdown',
    TOGGLE_STATUS: (serviceCenterId: number | string) => `/service-centers/${serviceCenterId}/toggle-status`,
    UPDATE: (serviceCenterId: number | string) => `/service-centers/${serviceCenterId}`
  },

  SERVICE_ROUTES: {
    CANCEL: (serviceRouteId: number | string) => `/service-routes/${serviceRouteId}/cancel`,
    COMPLETE: (serviceRouteId: number | string) => `/service-routes/${serviceRouteId}/complete`,
    EXPORT: '/service-routes/export',
    GET_ALL: '/service-routes',
    GET_BY_ID: (serviceRouteId: number | string) => `/service-routes/${serviceRouteId}`
  },
  SUPPORT_TICKETS: {
    ADMIN: {
      GET_ALL: '/support-tickets?createdByType=admin'
    },
    CREATE: '/support-tickets',
    DRIVER: {
      GET_ALL: '/support-tickets?createdByType=driver'
    },
    GET_BY_ID: (ticketId: number | string) => `/support-tickets/${ticketId}`,
    MY_TICKETS: '/support-tickets/my-tickets',
    TENANT: {
      GET_ALL: '/support-tickets?createdByType=tenant'
    },
    UPDATE: (ticketId: number | string) => `/support-tickets/${ticketId}`,
    USER: {
      GET_ALL: '/support-tickets?createdByType=user'
    }
  },
  SYSTEM_HEALTH: {
    BACKUP_CLEAR: '/system/backups/clear',
    BACKUP_DELETE: '/system/backups/delete',
    BACKUP_DOWNLOAD: '/system/backups/download',
    BACKUP_FILES: '/system/backups/files',
    BACKUPS: '/system/backups',
    COMMAND_RUN: (id: string) => `/system/commands/runs/${id}`,
    COMMANDS: '/system/commands',
    DELETE_FAILED_JOB: (uuid: string) => `/system/failed-jobs/${uuid}`,
    DELETE_LOG: (file: string) => `/system/logs/${encodeURIComponent(file)}`,
    DOWNLOAD_LOG: (file: string) => `/system/logs/${encodeURIComponent(file)}/download`,
    FAILED_JOB_BY_UUID: (uuid: string) => `/system/failed-jobs/${uuid}`,
    FAILED_JOBS: '/system/failed-jobs',
    FLUSH_FAILED_JOBS: '/system/failed-jobs',
    HEALTH: '/system/health',
    JOB_RUN: (id: string) => `/system/queue/runs/${id}`,
    LOG_ENTRIES: (file: string) => `/system/logs/${encodeURIComponent(file)}`,
    LOG_ENTRY: (file: string, id: number) =>
      `/system/logs/${encodeURIComponent(file)}/entries/${id}`,
    LOGS: '/system/logs',
    PENDING_JOB_BY_ID: (id: number | string) => `/system/queue/jobs/${id}`,
    PENDING_JOBS: '/system/queue/jobs',
    QUEUE_OVERVIEW: '/system/queue/overview',
    RESOURCES: '/system/resources',
    RETRY_ALL_FAILED_JOBS: '/system/failed-jobs/retry-all',
    RETRY_FAILED_JOB: (uuid: string) => `/system/failed-jobs/${uuid}/retry`,
    RUN_COMMAND: '/system/commands/run',
    RUN_PENDING_JOB: (id: number | string) => `/system/queue/jobs/${id}/run`,
    SPECS: '/system/specs'
  },
  TAGS: {
    LIST_DROPDOWN: '/tags/list-dropdown'
  },
  TENANT_ADMINS: {
    CREATE: '/tenant-admins',
    DEACTIVATE: (id: number | string) => `/tenant-admins/${id}/deactivate`,
    DELETE: (id: number | string) => `/tenant-admins/${id}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/tenant-admins',
    DOWNLOAD_IMPORT_TEMPLATE_TENANT: '/import-templates/tenant-admins-tenant',
    EXPORT: '/tenant-admins/export',
    GET_ALL: '/tenant-admins',
    GET_BY_ID: (id: number | string) => `/tenant-admins/${id}`,
    IMPORT: '/tenant-admins/import',
    IMPORT_BY_TENANT_ID: (tenantId: number | string) => `/tenants/${tenantId}/tenant-admins/import`,
    IMPORT_VALIDATE: '/tenant-admins/import/validate',
    IMPORT_VALIDATE_BY_TENANT_ID: (tenantId: number | string) => `/tenants/${tenantId}/tenant-admins/import/validate`,
    LIST_DROPDOWN: '/tenant-admins/list-dropdown',
    TOGGLE_STATUS: (id: number | string) => `/tenant-admins/${id}/toggle-status`,
    UPDATE: (id: number | string) => `/tenant-admins/${id}`
  },
  TENANT_BRANCHES: {
    CREATE: '/tenant-branches',
    DELETE: (branchId: number | string) => `/tenant-branches/${branchId}`,
    GET_ALL: '/tenant-branches',
    GET_BY_TENANT_ID: (tenantId: number | string) => `/tenant-branches/tenant/${tenantId}`,
    GET_ONE_BY_ID: (branchId: number | string) => `/tenant-branches/${branchId}`,
    LIST_DROPDOWN: '/tenant-branches/list-dropdown',
    UPDATE: (branchId: number | string) => `/tenant-branches/${branchId}`
  },
  TENANT_CONTRACTS: {
    CREATE: '/tenant-contracts',
    DELETE: (contractId: number | string) => `/tenant-contracts/${contractId}`,
    GET_ALL: '/tenant-contracts',
    GET_BY_ID: (contractId: number | string) => `/tenant-contracts/${contractId}`,
    GET_BY_TENANT: (tenantId: number | string) => `/tenant-contracts/tenant/${tenantId}`,
    LIST_DROPDOWN: '/tenant-contracts/list-dropdown',
    UPDATE: (contractId: number | string) => `/tenant-contracts/${contractId}`
  },
  TENANT_PERMISSIONS: {
    GET_ALL: '/tenant-permissions'
  },
  TENANT_ROLES: {
    CLONE: (id: number | string) => `/tenant-roles/${id}/clone`,
    CREATE: '/tenant-roles',
    DELETE: (id: number | string) => `/tenant-roles/${id}`,
    GET_ALL: '/tenant-roles',
    GET_BY_ID: (id: number | string) => `/tenant-roles/${id}`,
    LIST_DROPDOWN: '/tenant-roles/list-dropdown',
    UPDATE: (id: number | string) => `/tenant-roles/${id}`
  },
  TENANTS: {
    CONTRACT: {
      DELETE_ADMIN: (adminId: number | string) => `/tenant-admins/${adminId}`
    },
    DELETE: (tenantId: number | string) => `/tenants/${tenantId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/tenants',
    GET_ALL: '/tenants',
    GET_BY_ID: (tenantId: number | string) => `/tenants/${tenantId}`,
    GET_TENANT_ADMIN_BY_ID: (adminId: number | string) => `/tenant-admins/${adminId}`,
    GET_TENANT_ADMINS: (tenantId: number | string) => `tenant-admins/tenant/${tenantId}`,
    IMPORT: '/tenants/import',
    IMPORT_VALIDATE: '/tenants/import/validate',
    LIST_DROPDOWN: '/tenants/list-dropdown',
    POST_ADD_TENANT: '/tenants',
    POST_ADD_TENANT_ADMIN: '/tenant-admins',
    POST_GET_VEHICLE_MODELS: (tenantId: number | string) => `/tenants/${tenantId}/vehicle-models`,
    POST_UPDATE_TENANT: (tenantId: number | string) => `/tenants/${tenantId}?_method=put`,
    POST_UPDATE_TENANT_ADMIN: (adminId: number | string) => `/tenant-admins/${adminId}?_method=put`,
    PUT_TOGGLE_TENANT_STATUS: (tenantId: number | string) => `/tenants/${tenantId}/toggle-status`
  },
  TENANTS_ROUTES: {
    BULK_CHANGE_STATUS: '/tenant-routes/bulk-change-status',
    BULK_DELETE: '/tenant-routes/bulk-delete',
    CREATE: '/tenant-routes',
    DELETE: (routeId: number | string) => `/tenant-routes/${routeId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/tenant-routes',
    DOWNLOAD_IMPORT_TEMPLATE_CONTRACT: '/import-templates/tenant-routes-contract',
    DOWNLOAD_IMPORT_TEMPLATE_TENANT: '/import-templates/tenant-routes-tenant',
    EXPORT: '/tenant-routes/export',
    GET_ALL: '/tenant-routes',
    GET_BY_ID: (routeId: number | string) => `/tenant-routes/${routeId}`,
    GET_BY_TENANT_ID: (tenantId: number | string) => `/tenant-routes/tenant/${tenantId}`,
    IMPORT: '/tenant-routes/import',
    IMPORT_BY_CONTRACT_ID: (contractId: number | string) => `/tenant-contracts/${contractId}/tenant-routes/import`,
    IMPORT_BY_TENANT_ID: (tenantId: number | string) => `/tenants/${tenantId}/tenant-routes/import`,
    IMPORT_VALIDATE: '/tenant-routes/import/validate',
    IMPORT_VALIDATE_BY_CONTRACT_ID: (contractId: number | string) => `/tenant-contracts/${contractId}/tenant-routes/import/validate`,
    IMPORT_VALIDATE_BY_TENANT_ID: (tenantId: number | string) => `/tenants/${tenantId}/tenant-routes/import/validate`,
    LIST_DROPDOWN: '/tenant-routes/list-dropdown',
    PUT_UPDATE: (routeId: number | string) => `/tenant-routes/${routeId}`
  },
  TICKETS: {
    GET_ALL: '/tickets',
    GET_BY_ID: (ticketId: number | string) => `/tickets/${ticketId}`
  },
  TRIP_FEEDBACKS: {
    DELETE: (feedbackId: number | string) => `/trip-feedbacks/${feedbackId}`,
    GET_BY_DRIVER_ID: (driverId: number | string) => `/trip-feedbacks/driver/${driverId}`,
    GET_BY_TRIP_ID: (tripId: number | string) => `/trip-feedbacks/trip/${tripId}`,
    GET_BY_VEHICLE_ID: (vehicleId: number | string) => `/trip-feedbacks/vehicle/${vehicleId}`,
    LIST_DROPDOWN: '/trip-feedbacks/list-dropdown'
  },
  TRIP_PASSENGERS: {
    APPROVE_REPLACEMENT: (passengerId: number | string) => `/trip-passengers/${passengerId}/approve-replacement`,
    BULK_CHANGE_STATUS: '/trip-passengers/bulk-change-status',
    BULK_DELETE: '/trip-passengers/bulk-delete',
    DELETE: (passengerId: number | string) => `/trip-passengers/${passengerId}`,
    GET_BY_TRIP: (tripId: number | string) => `/trip-passengers/trip/${tripId}`,
    LIST_DROPDOWN: '/trip-passengers/list-dropdown',
    POST_BULK_CREATE: '/trip-passengers/bulk',
    POST_CREATE: '/trip-passengers',
    PUT_UPDATE: (passengerId: number | string) => `/trip-passengers/${passengerId}`,
    REJECT_REPLACEMENT: (passengerId: number | string) => `/trip-passengers/${passengerId}/reject-replacement`,
    REPLACEMENT_REQUESTS: '/trip-passengers/replacement-requests'
  },
  TRIP_VIOLATIONS: {
    DELETE: (violationId: number | string) => `/trip-violations/${violationId}`,
    GET_ALL: '/trip-violations',
    LIST_DROPDOWN: '/trip-violations/list-dropdown'
  },
  TRIPS: {
    APPROVE: (tripId: number | string) => `/trips/${tripId}/approve`,
    BACKLOG_CREATE: '/trips/backlog-create',
    BULK_CHANGE_STATUS: '/trips/bulk-change-status',
    BULK_DELETE: '/trips/bulk-delete',
    CANCEL: (tripId: number | string) => `/trips/${tripId}/cancel`,
    CREATE: '/trips',
    DELETE: (tripId: number | string) => `/trips/${tripId}`,
    DISAPPROVE: (tripId: number | string) => `/trips/${tripId}/disapprove`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/trips',
    EXPORT: '/trips/export',
    FINALIZE: (tripId: number | string) => `/trips/${tripId}/finalize`,
    GENERATE: '/trips/generate',
    GET_ALL: '/trips',
    GET_BY_DRIVER_ID: (driverId: number | string) => `/trips/driver/${driverId}`,
    GET_BY_ID: (tripId: number | string) => `/trips/${tripId}`,
    GET_BY_ROUTE_SHIFT_ID: (routeShiftId: number | string) => `/trips/route-shift/${routeShiftId}`,
    GET_BY_TENANT: (tenantId: number | string) => `/trips/tenant/${tenantId}`,
    GET_BY_USER_ID: (userId: number | string) => `/trips/user/${userId}`,
    GET_BY_VEHICLE_ID: (vehicleId: number | string) => `/trips/vehicle/${vehicleId}`,
    GET_CURRENT_BY_DRIVER_ID: (driverId: number | string) => `/trips/driver/${driverId}/current`,
    GET_CURRENT_BY_VEHICLE_ID: (vehicleId: number | string) => `/trips/vehicle/${vehicleId}/current`,
    GET_HISTORY: (tripId: number | string) => `/trips/${tripId}/history`,
    IMPORT: '/trips/import',
    IMPORT_VALIDATE: '/trips/import/validate',
    LIST_DROPDOWN: '/trips/list-dropdown',
    SPLIT: (tripId: number | string) => `/trips/${tripId}/split`,
    UPDATE: (tripId: number | string) => `/trips/${tripId}`,
    UPDATE_POLYLINE: (tripId: number | string) => `/trips/${tripId}/polyline`,
    UPDATE_STATUS: (tripId: number | string) => `/trips/${tripId}/status`
  },
  USERS: {
    BULK_CHANGE_STATUS: '/users/bulk-change-status',
    BULK_DELETE: '/users/bulk-delete',
    CREATE: '/users',
    DELETE: (userId: number | string) => `/users/${userId}`,
    DELETE_PARENT_RELATIONSHIP: (childId: number | string, parentId: number | string) => `/users/${childId}/remove-parent/${parentId}`,
    DELETE_USER: (userId: number | string) => `/users/${userId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/users',
    DOWNLOAD_IMPORT_TEMPLATE_CONTRACT: '/import-templates/users-contract',
    DOWNLOAD_IMPORT_TEMPLATE_TENANT: '/import-templates/users-tenant',
    EXPORT: '/users/export',
    GET_ALL: '/users',
    GET_BY_ID: (userId: number | string) => `/users/${userId}`,
    GET_CHILDREN_BY_USER_ID: (userId: number | string) => `/users/${userId}/children`,
    GET_PARENTS_BY_USER_ID: (userId: number | string) => `/users/${userId}/parents`,
    GET_USER_BY_ID: (userId: number | string) => `/users/${userId}`,
    GET_USER_ROUTE_SHIFTS_BY_USER_ID: (userId: number | string) => `/users/${userId}/route-shifts`,
    GET_USERS_BY_TENANT: (tenantId: number | string) => `users/tenant/${tenantId}`,
    GET_USERS_BY_TENANT_ID: (tenantId: number | string) => `users/tenant/${tenantId}`,
    IMPORT: '/users/import',
    IMPORT_BY_CONTRACT_ID: (contractId: number | string) => `/tenant-contracts/${contractId}/users/import`,
    IMPORT_BY_TENANT_ID: (tenantId: number | string) => `/tenants/${tenantId}/users/import`,
    IMPORT_VALIDATE: '/users/import/validate',
    IMPORT_VALIDATE_BY_CONTRACT_ID: (contractId: number | string) => `/tenant-contracts/${contractId}/users/import/validate`,
    IMPORT_VALIDATE_BY_TENANT_ID: (tenantId: number | string) => `/tenants/${tenantId}/users/import/validate`,
    LIST_DROPDOWN: '/users/list-dropdown',
    POST_ADD_TENANT_RIDER: '/users',
    POST_ASSIGN_PARENT: '/users/assign-parent',
    POST_CREATE_AND_ASSIGN_CHILD: (parentId: number | string) => `/users/${parentId}/create-and-assign-child`,
    POST_CREATE_AND_ASSIGN_PARENT: (childId: number | string) => `/users/${childId}/create-and-assign-parent`,
    POST_TOGGLE_USER_STATUS: (userId?: number | string) => `/users/${userId}/toggle-status`,
    PUT_UPDATE_USER: (userId?: number | string) => `/users/${userId}`,
    UPDATE: (userId: number | string) => `/users/${userId}?_method=put`
  },
  VEHICLE_ATTRIBUTES: {
    GET_MODELS_BY_BRAND_ID: (brandId: number | string) => `/vehicle-models/brand/${brandId}`,
    GET_TYPES_BY_MODEL_ID: (modelId: number | string) => `/vehicle-model-types/model/${modelId}`
  },
  VEHICLE_BRANDS: {
    DELETE_BRAND: (brandId: number | string) => `/vehicle-brands/${brandId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/vehicle-brands',
    EXPORT: '/vehicle-brands/export',
    GET_ALL_BRANDS: '/vehicle-brands',
    IMPORT: '/vehicle-brands/import',
    IMPORT_VALIDATE: '/vehicle-brands/import/validate',
    LIST_DROPDOWN: '/vehicle-brands/list-dropdown',
    POST_CREATE: '/vehicle-brands',
    POST_REORDER: '/vehicle-brands/reorder',
    POST_UPDATE: (brandId: number | string) => `/vehicle-brands/update/${brandId}`
  },
  VEHICLE_MODEL_TYPES: {
    DELETE_TYPE: (typeId: number | string) => `/vehicle-model-types/${typeId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/vehicle-model-types',
    EXPORT: '/vehicle-model-types/export',
    GET_ALL_TYPES: '/vehicle-model-types',
    GET_TYPE_BY_ID: (typeId: number | string) => `/vehicle-model-types/${typeId}`,
    GET_TYPES_BY_MODEL_ID: (modelId: number | string) => `/vehicle-model-types/model/${modelId}`,
    IMPORT: '/vehicle-model-types/import',
    IMPORT_VALIDATE: '/vehicle-model-types/import/validate',
    LIST_DROPDOWN: '/vehicle-model-types/list-dropdown',
    POST_CREATE: '/vehicle-model-types',
    POST_REORDER: '/vehicle-model-types/reorder',
    POST_UPDATE: (typeId: number | string) => `/vehicle-model-types/update/${typeId}`
  },
  VEHICLE_MODELS: {
    DELETE_MODEL: (modelId: number | string) => `/vehicle-models/${modelId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/vehicle-models',
    EXPORT: '/vehicle-models/export',
    GET_ALL_MODELS: '/vehicle-models',
    GET_MODELS_BY_BRAND_ID: (brandId: number | string) => `/vehicle-models/brand/${brandId}`,
    GET_MODELS_BY_MODEL_ID: (modelId: number | string) => `/vehicle-models/${modelId}`,
    IMPORT: '/vehicle-models/import',
    IMPORT_VALIDATE: '/vehicle-models/import/validate',
    LIST_DROPDOWN: '/vehicle-models/list-dropdown',
    POST_CREATE: '/vehicle-models',
    POST_REORDER: '/vehicle-models/reorder',
    POST_UPDATE: (modelId: number | string) => `/vehicle-models/update/${modelId}`
  },
  VEHICLES: {
    BULK_CHANGE_STATUS: '/vehicles/bulk-change-status',
    BULK_DELETE: '/vehicles/bulk-delete',
    CREATE: '/vehicles',
    DELETE: (vehicleId: number | string) => `/vehicles/${vehicleId}`,
    DOWNLOAD_IMPORT_TEMPLATE: '/import-templates/vehicles',
    DROPDOWN: '/vehicles/dropdown',
    EXPORT: '/vehicles/export',
    GET_ALL: '/vehicles',
    GET_BY_CONTRACT: '/vehicles/by-contract',
    GET_BY_ID: (vehicleId: number | string) => `/vehicles/${vehicleId}`,
    GET_MILEAGE_HISTORIES: (vehicleId: number | string) =>
      `/vehicles/${vehicleId}/mileage-histories`,
    IMPORT: '/vehicles/import',
    IMPORT_VALIDATE: '/vehicles/import/validate',
    LIST_DROPDOWN: '/vehicles/list-dropdown',
    TOGGLE_STATUS: (vehicleId: number | string) => `/vehicles/${vehicleId}/toggle-status`,
    UNASSIGN_DRIVER: (vehicleId: number | string) => `/vehicles/${vehicleId}/unassign-driver`,
    UPDATE: (vehicleId: number | string) => `/vehicles/${vehicleId}?_method=put`
  },

  VEHICLES_OPTIONS: {
    CREATE: '/special-options',
    DELETE: (optionId: number | string) => `/special-options/${optionId}`,
    EXPORT: '/special-options/export',
    GET_ALL: '/special-options',
    GET_BY_ID: (optionId: number | string) => `/special-options/${optionId}`,
    LIST_DROPDOWN: '/special-options/list-dropdown',
    UPDATE: (optionId: number | string) => `/special-options/update/${optionId}`
  }
} as const

declare global {
  type ENDPOINTS = DeepValueOf<typeof ENDPOINTS>
}
